import {Alert} from 'src/design/atoms/Alert';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconAlertTriangle} from '@tabler/icons-react';
import type {FC} from 'react';
import {useState} from 'react';
import {Button} from 'src/design/atoms/Button';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {InfoAlert} from 'src/design/molecules/InfoAlert';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {FailedOutboxItem} from './pendingUploads';
import {requestDrain} from './syncScheduler';
import type {OutboxItem} from './types';
import {useConnectivity} from './useConnectivity';

export interface PendingSyncBannerCopy {
  /** What one queued item is called, e.g. 'form submission'. */
  noun: string;
  nounPlural: string;
  /** Closing sentence while offline, e.g. 'They will be sent automatically…'. */
  offlineTail: string;
  discardTitle: string;
  /** Tail of the discard confirmation, e.g. 'it will not be submitted.' */
  discardBodyTail: string;
  discardConfirmLabel: string;
}

export interface PendingSyncBannerProps {
  pendingCount: number;
  failedItems: FailedOutboxItem[];
  /** Names one queued item for the failure list and the discard dialog. */
  itemLabel: (item: OutboxItem) => string;
  onRetry: (item: OutboxItem) => Promise<void>;
  onDiscard: (item: OutboxItem) => Promise<void>;
  copy: PendingSyncBannerCopy;
}

/**
 * Banner for queued offline work: a count while everything is merely waiting,
 * and a per-item retry/discard list once an item has permanently failed.
 * Renders nothing when the outbox holds nothing for this domain.
 */
export const PendingSyncBanner: FC<PendingSyncBannerProps> = ({
  pendingCount,
  failedItems,
  itemLabel,
  onRetry,
  onDiscard,
  copy,
}) => {
  const {isOffline} = useConnectivity();
  const {setAlert} = useAlert();
  const [itemToDiscard, setItemToDiscard] = useState<OutboxItem | undefined>();

  const handleRetry = async (item: OutboxItem) => {
    try {
      await onRetry(item);
      requestDrain();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleDiscardConfirmed = async () => {
    if (!itemToDiscard) return;
    const item = itemToDiscard;
    setItemToDiscard(undefined);
    try {
      await onDiscard(item);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  if (failedItems.length === 0) {
    if (pendingCount === 0) return null;
    const subject =
      pendingCount === 1 ? `${copy.noun} is` : `${copy.nounPlural} are`;
    const tail = isOffline ? copy.offlineTail : 'They are syncing now.';
    return (
      <InfoAlert variant="info" mb="md">
        {`${pendingCount} ${subject} waiting to sync. ${tail}`}
      </InfoAlert>
    );
  }

  return (
    <>
      <Alert
        color="yellow"
        icon={<IconAlertTriangle />}
        title={`${failedItems.length} ${
          failedItems.length === 1 ? copy.noun : copy.nounPlural
        } failed to sync`}
        mb="md">
        <Stack gap="xs">
          {pendingCount > 0 && (
            <Text size="sm">
              {`${pendingCount} more ${
                pendingCount === 1 ? 'is' : 'are'
              } still waiting to sync.`}
            </Text>
          )}
          {failedItems.map(({item, error}) => (
            <Group
              key={item.id}
              justify="space-between"
              wrap="nowrap"
              align="flex-start">
              <Stack gap={0}>
                <Text size="sm" fw={500}>
                  {itemLabel(item)}
                </Text>
                <Text size="xs" c="dimmed">
                  {error}
                </Text>
              </Stack>
              <Group gap="xs" wrap="nowrap">
                <Button
                  variant="outline"
                  size="xs"
                  disabled={isOffline}
                  onClick={() => handleRetry(item)}>
                  Retry
                </Button>
                <Button
                  variant="subtle"
                  color="red"
                  size="xs"
                  onClick={() => setItemToDiscard(item)}>
                  Discard
                </Button>
              </Group>
            </Group>
          ))}
        </Stack>
      </Alert>
      <ConfirmDialog
        opened={itemToDiscard !== undefined}
        onClose={() => setItemToDiscard(undefined)}
        onConfirm={handleDiscardConfirmed}
        title={copy.discardTitle}
        body={`Discarding "${
          itemToDiscard ? itemLabel(itemToDiscard) : ''
        }" permanently removes it from this device — ${copy.discardBodyTail}`}
        confirmationButtonColor="red"
        buttonTextYes={copy.discardConfirmLabel}
        buttonTextNo="Keep"
      />
    </>
  );
};
